import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import shallow from 'zustand/shallow';
import dayjs from 'dayjs';
import { IPackage, IPackagePrice } from '@root/types/package';
import { useDefaultPackageState, useUnBindDevicesState } from '@root/state';
import { packageApi } from '@root/api';
import { DATE_FORMAT, EPayMode, PROMOTIONS_ACTIVITY_ID } from '@root/constant';
import { getQueryString, isInApp } from '@root/shared';
import { loggerOperatorOnTrack } from '@root/logger/loggerOperator';
import { getCurrentToken } from '@root/shared/currentAuth';
import Toast from '@root/components/toast';

const flagPayMode = 'promotions';

const usePromotions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetUnBindUpdateTime = useUnBindDevicesState(
    (state) => state.resetUpdateTime,
  );
  const navigateType = useNavigationType();
  const [loading, setLoading] = useState(0); // 0, 1, 2 loading， success，fail
  const [activityInfo, setActivityInfo] = useState<any>({});
  const {
    selectedPrice,
    curPackage,
    statePayMode,
    setPackage,
    setSelectedPrice,
    resetInitData,
  } = useDefaultPackageState(
    (state) => ({
      curPackage: state.packageData,
      selectedPrice: state.selectedPrice,
      subscribedInfo: state.subscribedInfo,
      updateTime: state.updateTime,
      statePayMode: state.payMode,
      setPackage: state.setPackage,
      setSelectedPrice: state.setSelectedPrice,
      setSubscribedInfo: state.updateSubscribedInfo,
      resetInitData: state.resetInitData,
    }),
    shallow,
  );

  const sensorOriginal = getQueryString('original') || '';

  const getPackageData = useCallback(async () => {
    setLoading(0);

    try {
      const [data, activityRes]: any = await Promise.all([
        packageApi.getPackages({
          level: 15,
          pageSize: 1,
          payMode: EPayMode.PayPal,
        }),
        packageApi.getActivity(PROMOTIONS_ACTIVITY_ID),
      ]);

      const _data = data?.list?.[0] || {};

      if (_data.goodsPriceList?.length) {
        // 年订阅活动页面目前只有月订阅 一台设备的套餐
        const _selectedPrice =
          _data.goodsPriceList.find(
            (i: IPackagePrice) => i.planUnit === 'year' && i.buyNum === 1,
          ) || _data.goodsPriceList[0];

        setSelectedPrice(_selectedPrice);
      }

      setPackage(_data as IPackage, flagPayMode);

      setActivityInfo(activityRes || {});

      setLoading(1);
    } catch (error) {
      setLoading(2);
    }
  }, [setPackage, setSelectedPrice]);

  // 神策数据上报
  const sensorTrack = useCallback((event: string, params = {}) => {
    const accessToken = getCurrentToken();
    loggerOperatorOnTrack(event, {
      promotions_id: PROMOTIONS_ACTIVITY_ID,
      is_login: !!accessToken,
      ...params,
    });
  }, []);

  const onClickPay = useCallback(() => {
    const accessToken = getCurrentToken();

    // status 0 未开始 1 进行中 2 已结束
    sensorTrack('clickAnnualPromotionsPurchase');

    if (activityInfo.status === 0) {
      Toast.info(t('event_no_start'));
      return;
    }

    if (activityInfo.status === 2) {
      Toast.info(t('event_has_expired'));
      return;
    }

    if (!accessToken) {
      navigate(
        `/login?redirectUrl=${encodeURIComponent(`/promotions/selectDevice`)}`,
      );
      return;
    }

    navigate(`/promotions/selectDevice?activityId=${PROMOTIONS_ACTIVITY_ID}`);
  }, [activityInfo.status, navigate, sensorTrack, t]);

  useEffect(() => {
    if (
      (navigateType === 'PUSH' || !usePromotions.showAnnualPromotions) &&
      loading === 1
    ) {
      usePromotions.showAnnualPromotions = true;
      sensorTrack('showAnnualPromotions', {
        start_time: activityInfo.startTime
          ? dayjs(activityInfo.startTime).format(DATE_FORMAT)
          : '',
        end_time: activityInfo.endTime
          ? dayjs(activityInfo.endTime).format(DATE_FORMAT)
          : '',
        original: sensorOriginal,
        show_platform: isInApp ? 'app' : 'h5',
      });
    }
  }, [
    activityInfo.endTime,
    activityInfo.startTime,
    loading,
    navigateType,
    sensorOriginal,
    sensorTrack,
  ]);

  useEffect(() => {
    resetUnBindUpdateTime();
  }, [resetUnBindUpdateTime]);

  useEffect(() => {
    if (statePayMode !== flagPayMode) {
      resetInitData();
    }
  }, [resetInitData, statePayMode]);

  useEffect(() => {
    getPackageData();
  }, [getPackageData]);

  return {
    selectedPrice,
    activityInfo,
    curPackage,
    loading,
    getPackageData,
    onClickPay,
  };
};

usePromotions.showAnnualPromotions = false;

export default usePromotions;
